import { useState } from 'react'
import { LazyAvatar } from '@/components/common/LazyAvatar'
import { constructProxyUrl } from '@/utils/proxy'
import { getCTALabel } from '@/components/mediaBuying/adCreatives/screens/adContent/CTADropdown'
import iconPlaceholder from '@/assets/images/media-buying/icon-placeholder.png'
import { Media } from '@/components/mediaBuying/adCreatives/screens/mediaCrop/Media.jsx'
import { truncateHeadline, truncateDescription, isTextSingleLine } from '../ad-preview'
import { getFormatMediaProps } from './getFormatMediaProps'

const CarouselPreviewVertical = ({
    assets: passedAssets,
    primaryText,
    cta,
    pageTitle,
    pageIcon,
    showDefaultPrimaryText,
}) => {
    const [currentIndex, setCurrentIndex] = useState(0)
    const [assets] = useState(passedAssets?.map((asset) => ({ ...asset, url: constructProxyUrl(asset.url) })) || [])

    const handlePrevious = () => {
        setCurrentIndex((prev) => (prev > 0 ? prev - 1 : assets.length - 1))
    }

    const handleNext = () => {
        setCurrentIndex((prev) => (prev < assets.length - 1 ? prev + 1 : 0))
    }

    const currentAsset = assets[currentIndex] || {}
    const assetVariant = currentAsset.variations?.find((x) => x.format === 'vertical')
    const cropData = assetVariant?.crop_data

    if (!assetVariant) {
        return (
            <div className="tw:w-[305px] tw:h-[540px] tw:bg-gray-800 tw:!rounded-[23px] tw:flex tw:items-center tw:justify-center tw:text-gray-400">
                No vertical preview
            </div>
        )
    }

    return (
        <div className="tw:w-[305px] tw:h-[540px] tw:bg-black tw:!rounded-[23px] tw:overflow-hidden tw:relative tw:flex tw:flex-col text-white">
            {/* Full background image */}
            <div className="tw:absolute tw:inset-0">
                <Media
                    type={currentAsset?.type}
                    url={currentAsset?.url || ''}
                    canvasWidth={assetVariant?.cropped ? cropData?.width : 241}
                    canvasHeight={assetVariant?.cropped ? cropData?.height : 428}
                    cropData={assetVariant?.cropped ? cropData : null}
                    isProxied={true}
                    autoStart={false}
                    minHeight={540}
                    minWidth={305}
                    startTime={currentAsset?.trim_data?.start}
                    endTime={currentAsset?.trim_data?.end}
                />
            </div>

            {/* Navigation Arrows - Side mounted */}
            {assets.length > 1 && (
                <>
                    <button
                        onClick={handlePrevious}
                        className="tw:absolute tw:left-2 tw:top-1/2 tw:-translate-y-1/2 tw:z-20 tw:w-11 tw:h-11 tw:bg-white/20 tw:backdrop-blur-sm tw:!rounded-full tw:flex tw:items-center tw:justify-center tw:!border-0 tw:transition-all hover:tw:bg-white/30"
                    >
                        <span className="tw:text-white tw:font-bold tw:text-xl">‹</span>
                    </button>
                    <button
                        onClick={handleNext}
                        className="tw:absolute tw:right-2 tw:top-1/2 tw:-translate-y-1/2 tw:z-20 tw:w-11 tw:h-11 tw:bg-white/20 tw:backdrop-blur-sm tw:!rounded-full tw:flex tw:items-center tw:justify-center tw:!border-0 tw:transition-all hover:tw:bg-white/30"
                    >
                        <span className="tw:text-white tw:font-bold tw:text-xl">›</span>
                    </button>
                </>
            )}

            {/* Content - positioned at bottom */}
            <div className="tw:relative tw:flex tw:z-10 tw:mt-auto tw:p-3">
                <div className="w-100">
                    <div className="tw:relative tw:z-10 tw:flex tw:items-center tw:p-2 tw:pb-1 tw:gap-2">
                        <LazyAvatar
                            src={pageIcon}
                            placeholder={iconPlaceholder}
                            alt={'Page Image'}
                            className="tw:w-6 tw:h-6 tw:rounded-full"
                        />
                        <div className="tw:flex tw:items-center tw:text-sm">
                            <span className="tw:font-medium overflow-ellipse tw:max-w-[180px]">{pageTitle}</span>
                        </div>
                    </div>

                    {/* Primary Text */}
                    {(currentAsset.headline || showDefaultPrimaryText) && (
                        <div className="tw:text-sm tw:leading-snug tw:pl-4 overflow-ellipse tw:w-[200px] text-nowrap">
                            {showDefaultPrimaryText
                                ? "Dreaming of a carefree beach escape? We'll find the perfect tour to bring you sun sea and total relaxation."
                                : currentAsset.headline}
                        </div>
                    )}

                    {/* Description - Only show if primary text is single line */}
                    {primaryText && (
                        <div className="tw:relative tw:z-10 tw:flex tw:flex-col tw:gap-1">
                            {primaryText?.length >= 42 ? (
                                <span className="tw:text-xs tw:leading-snug tw:pl-4 tw:text-white tw:w-[240px] overflow-ellipse tw:whitespace-nowrap tw:mt-1">
                                    {`${primaryText.slice(0, 33)}  ...more`}
                                </span>
                            ) : (
                                <div className="tw:text-xs tw:leading-snug tw:pl-4 tw:text-white tw:w-[220px] overflow-ellipse tw:whitespace-nowrap tw:mt-1">
                                    {primaryText}
                                </div>
                            )}
                        </div>
                    )}

                    {/* CTA Section */}
                    <div className="tw:bg-transparent tw:bg-opacity-95 tw:rounded-lg tw:pt-3 tw:px-2 tw:text-black">
                        <button className="tw:w-full tw:bg-white tw:text-black tw:py-2 tw:px-4 tw:!rounded-md tw:text-sm tw:font-semibold">
                            {getCTALabel(cta)}
                        </button>
                    </div>

                    <div className="tw:flex tw:items-center tw:justify-between tw:mt-2 tw:text-xs">
                        <div className="tw:bg-black/70  tw:text-sm tw:rounded-md tw:ml-2 tw:px-2 tw:items-left">
                            Sponsored
                        </div>
                        <div className="tw:bg-transparent tw:text-sm tw:absolute tw:right-5">...</div>
                    </div>
                </div>
                <div className="tw:w-[20px] tw:flex tw:flex-col tw:justify-end tw:items-center tw:ml-1">
                    <i className="bi bi-hand-thumbs-up tw:mb-2 tw:text-xl" />
                    <i className="bi bi-chat-left tw:pb-8 tw:text-xl" />
                </div>
            </div>
        </div>
    )
}

export const AdPreviewVertical = ({
    assetVariant,
    primaryText = '',
    headline = '',
    description = '',
    cta = 'Learn More',
    pageTitle = 'Page Name',
    pageIcon = iconPlaceholder,
    showDefaultPrimaryText = false,
    assetData = {},
    carouselAssets = [],
    adCreativeFormat,
    cropStateFromMediaCrop = [],
}) => {
    // Use first variation if arrays are passed and apply truncation
    const rawPrimaryText = Array.isArray(primaryText) ? primaryText[0] : primaryText
    const rawHeadline = Array.isArray(headline) ? headline[0] : headline
    const rawDescription = Array.isArray(description) ? description[0] : description

    // Apply truncation rules
    const displayPrimaryText = rawPrimaryText // Keep primary text unchanged
    const displayHeadline = truncateHeadline(rawHeadline) // Truncate headline to 40 chars

    // Show description only if headline takes 1 line (narrower container for vertical)
    const headlineIsSingleLine = isTextSingleLine(displayHeadline, 200, 14)
    const displayDescription = headlineIsSingleLine ? truncateDescription(rawDescription) : ''

    const mediaProps = getFormatMediaProps({
        format: 'vertical',
        cropStateFromMediaCrop,
        assetData,
        assetVariant,
        defaultWidth: 241,
        defaultHeight: 428,
    })

    const hasMedia = !!(
        assetVariant ||
        assetData?.url ||
        cropStateFromMediaCrop?.find((c) => c.id === 'vertical')?.originalUrl
    )

    return (
        <div className="d-flex justify-content-start align-items-center overflow-x-hidden">
            {/* Check if we have carousel assets */}
            {adCreativeFormat === 'carousel' ? (
                <CarouselPreviewVertical
                    assets={carouselAssets}
                    primaryText={displayPrimaryText}
                    description={displayDescription}
                    cta={cta}
                    pageTitle={pageTitle}
                    pageIcon={pageIcon}
                    showDefaultPrimaryText={showDefaultPrimaryText}
                />
            ) : !hasMedia ? (
                <div className="tw:w-[305px] tw:h-[540px] tw:bg-gray-800 tw:!rounded-[23px] tw:flex tw:items-center tw:justify-center tw:text-gray-400">
                    No vertical preview available
                </div>
            ) : (
                /* Vertical - prefer cropStateFromMediaCrop (same as Set Media Crop) > assetData > assetVariant */
                <div className="tw:w-[305px] tw:h-[540px] tw:bg-black tw:!rounded-[23px] tw:overflow-hidden tw:relative tw:flex tw:flex-col text-white">
                    {/* Full background image */}
                    <div className="tw:absolute tw:inset-0">
                        <Media
                            type={assetData?.type}
                            url={mediaProps.url}
                            aspect={assetVariant?.crop_data?.ratio || '9:16'}
                            canvasWidth={mediaProps.canvasWidth}
                            canvasHeight={mediaProps.canvasHeight}
                            cropData={mediaProps.cropData}
                            isProxied={true}
                            autoStart={false}
                            startTime={assetVariant?.adCreativeAsset?.trim_data?.start ?? assetData?.trim_data?.start}
                            endTime={assetVariant?.adCreativeAsset?.trim_data?.end ?? assetData?.trim_data?.end}
                            thumbnails={assetData?.thumbnails}
                        />
                    </div>

                    {/* Content - positioned at bottom */}
                    <div className="tw:relative tw:flex tw:z-10 tw:mt-auto tw:p-3">
                        <div className="w-100">
                            <div className="tw:relative tw:z-10 tw:flex tw:items-center tw:p-2 tw:pb-1 tw:gap-2">
                                <LazyAvatar
                                    src={pageIcon}
                                    placeholder={iconPlaceholder}
                                    alt={'Page Image'}
                                    className="tw:w-6 tw:h-6 tw:rounded-full"
                                />
                                <div className="tw:flex tw:items-center tw:text-sm">
                                    <span className="tw:font-medium overflow-ellipse tw:max-w-[180px]">
                                        {pageTitle}
                                    </span>
                                </div>
                            </div>

                            {/* Primary Text */}
                            {(displayPrimaryText || showDefaultPrimaryText) && (
                                <div className="tw:text-sm tw:leading-snug tw:pl-4 overflow-ellipse tw:w-[200px] text-nowrap">
                                    {showDefaultPrimaryText
                                        ? "Dreaming of a carefree beach escape? We'll find the perfect tour to bring you sun sea and total relaxation."
                                        : displayPrimaryText}
                                </div>
                            )}

                            {/* Description - Only show if primary text is single line */}
                            {displayDescription && (
                                <div className="tw:text-xs tw:leading-snug tw:pl-4 tw:text-gray-600 tw:w-[200px] tw:mt-1">
                                    {displayDescription}
                                </div>
                            )}

                            {/* CTA Section */}
                            <div className="tw:bg-transparent tw:bg-opacity-95 tw:rounded-lg tw:pt-3 tw:px-2 tw:text-black">
                                <button className="tw:w-full tw:bg-white tw:text-black tw:py-2 tw:px-4 tw:!rounded-md tw:text-sm tw:font-semibold">
                                    {getCTALabel(cta)}
                                </button>
                            </div>

                            <div className="tw:flex tw:items-center tw:justify-between tw:mt-2 tw:text-xs">
                                <div className="tw:bg-black/70  tw:text-sm tw:rounded-md tw:ml-2 tw:px-2 tw:items-left">
                                    Sponsored
                                </div>
                                <div className="tw:bg-transparent tw:text-sm tw:absolute tw:right-5">...</div>
                            </div>
                        </div>
                        <div className="tw:w-[20px] tw:flex tw:flex-col tw:justify-end tw:items-center tw:ml-1">
                            <i className="bi bi-hand-thumbs-up tw:mb-2 tw:text-xl" />
                            <i className="bi bi-chat-left tw:pb-8 tw:text-xl" />
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
